import { useState } from 'react';
import { trpc } from '../../lib/trpc';

interface MediaCleanupInterfaceProps {
  className?: string;
}

interface MediaWithUploader {
  id: string;
  cloudinaryPublicId: string;
  cloudinaryUrl: string;
  entityType: string;
  entityId: string;
  uploadedBy: {
    id: string;
    name?: string;
    email?: string;
  };
  filename: string;
  mimeType: string;
  width?: number;
  height?: number;
  sizeBytes: number;
  status: string;
  isPublished: boolean;
  tags: string[];
  caption?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  sizeFormatted?: string;
}

export function MediaCleanupInterface({ className = '' }: MediaCleanupInterfaceProps) {
  const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
  const [deleteFromCloudinary, setDeleteFromCloudinary] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Get orphan detection report
  const { data: orphanReport, isPending: reportLoading, refetch: refetchReport } = 
    trpc.media.getOrphanReport.useQuery();

  // Get cleanup eligible media with details
  const { data: cleanupEligible, isPending: eligibleLoading, refetch: refetchEligible } = 
    trpc.media.getCleanupEligible.useQuery();

  // Bulk cleanup mutation
  const bulkCleanupMutation = trpc.media.adminBulkCleanup.useMutation({
    onSuccess: () => {
      setSelectedMedia([]);
      setShowConfirmation(false);
      refetchReport();
      refetchEligible();
    },
  });

  // Dry run mutation for preview
  const dryRunMutation = trpc.media.adminBulkCleanup.useMutation();

  const handleSelectAll = () => {
    if (!cleanupEligible?.media) return;
    
    const mediaList = cleanupEligible.media as unknown as MediaWithUploader[];
    if (selectedMedia.length === mediaList.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(mediaList.map((m) => m.id));
    }
  };

  const handleSelectMedia = (mediaId: string) => {
    setSelectedMedia(prev => 
      prev.includes(mediaId) 
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const handleDryRun = async () => {
    if (selectedMedia.length === 0) return;

    try {
      const result = await dryRunMutation.mutateAsync({
        mediaIds: selectedMedia,
        deleteFromCloudinary,
        dryRun: true,
      });
      
      alert(`Dry Run Results:
- ${result.eligibleCount} files would be deleted
- ${result.referencedCount} files are still referenced
- Total size: ${formatBytes(result.totalSize)}
${result.referencedCount > 0 ? '\nSome files cannot be deleted due to references.' : ''}`);
    } catch (error) {
      console.error('Dry run failed:', error);
    }
  };

  const handleCleanup = async () => {
    if (selectedMedia.length === 0) return;

    try {
      const result = await bulkCleanupMutation.mutateAsync({
        mediaIds: selectedMedia,
        deleteFromCloudinary,
        dryRun: false,
      });

      alert(`Cleanup Complete:
- ${result.deletedFromDatabase} files deleted from database
- ${result.deletedFromCloudinary} files deleted from Cloudinary
- ${result.cloudinaryErrors.length} Cloudinary errors
- Total size freed: ${formatBytes(result.totalSize)}`);
    } catch (error) {
      console.error('Cleanup failed:', error);
    }
  };

  if (reportLoading || eligibleLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  const selectedCount = selectedMedia.length;
  const mediaList = (cleanupEligible?.media ?? []) as unknown as MediaWithUploader[];
  const selectedSize = mediaList
    .filter((m) => selectedMedia.includes(m.id))
    .reduce((sum, m) => sum + m.sizeBytes, 0);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Summary */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Cleanup Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {orphanReport?.summary.totalOrphans || 0}
            </div>
            <div className="text-sm text-red-700">Orphaned Files</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {formatBytes(orphanReport?.summary.totalSize || 0)}
            </div>
            <div className="text-sm text-orange-700">Total Size</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {selectedCount}
            </div>
            <div className="text-sm text-green-700">Selected for Cleanup</div>
          </div>
        </div>
      </div>

      {/* Cleanup Options */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cleanup Options</h3>
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={deleteFromCloudinary}
              onChange={(e) => setDeleteFromCloudinary(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-info-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Delete files from Cloudinary (recommended)
            </span>
          </label>
          
          <div className="flex space-x-4">
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              {selectedMedia.length === mediaList.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <button
              onClick={handleDryRun}
              disabled={selectedCount === 0 || dryRunMutation.isPending}
              className="px-4 py-2 text-sm bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {dryRunMutation.isPending ? 'Running...' : 'Dry Run'}
            </button>
            
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={selectedCount === 0}
              className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete Selected ({selectedCount})
            </button>
          </div>

          {selectedCount > 0 && (
            <div className="text-sm text-gray-600">
              Selected: {selectedCount} files, {formatBytes(selectedSize)}
            </div>
          )}
        </div>
      </div>

      {/* Media List */}
      <div className="bg-gray-50 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Orphaned Media Files</h3>
          <p className="text-sm text-gray-600 mt-1">
            Files marked as deleted and not referenced by any entity
          </p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {mediaList.map((media) => (
            <div key={media.id} className="px-6 py-4 flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedMedia.includes(media.id)}
                onChange={() => handleSelectMedia(media.id)}
                className="rounded border-gray-300 text-primary-600 focus:ring-info-500"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-4">
                  <img
                    src={media.cloudinaryUrl}
                    alt={media.filename}
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {media.filename}
                    </div>
                    <div className="text-sm text-gray-500">
                      {media.entityType} • {formatBytes(media.sizeBytes)} • 
                      Deleted: {media.deletedAt ? new Date(media.deletedAt).toLocaleDateString() : 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-400">
                      Uploader: {media.uploadedBy.name || media.uploadedBy.email}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-gray-500">
                {media.sizeFormatted}
              </div>
            </div>
          ))}
          
          {mediaList.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">
              No orphaned media files found. Great job keeping things clean! 🎉
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-50 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Cleanup</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete {selectedCount} media files? 
              This action cannot be undone.
            </p>
            <div className="text-sm text-gray-500 mb-6">
              • Files will be removed from database
              {deleteFromCloudinary && (
                <>
                  <br />• Files will be deleted from Cloudinary
                </>
              )}
              <br />• Total size: {formatBytes(selectedSize)}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanup}
                disabled={bulkCleanupMutation.isPending}
                className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {bulkCleanupMutation.isPending ? 'Deleting...' : 'Delete Files'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}